import { useEffect, useRef, useState } from 'react';
import { trustStats } from '@/mocks/homeData';

function AnimatedNumber({ target, suffix, duration = 2000 }: { target: string; suffix: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const num = parseInt(target, 10);
    if (isNaN(num)) return;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * num));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function TrustStats() {
  return (
    <section className="bg-[#F5F7FA] border-y border-gray-100 py-10 md:py-14">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {trustStats.map((stat, index) => (
            <div
              key={index}
              className={`text-center py-4 px-4 ${
                index < trustStats.length - 1 ? 'border-r border-gray-200' : ''
              }`}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1E5FAD] mb-2 leading-none">
                <AnimatedNumber target={stat.number} suffix={stat.suffix} />
              </div>
              <p className="text-xs md:text-sm text-[#666666] leading-relaxed mt-1">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
